<template>
  <div v-if="automations.length" class="space-y-3">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          {{ tt('Automation', 'Avtomatizacija') }}
        </h4>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{
            tt(
              'Countdowns and timer dials for switch timers, schedules and motion lights',
              'Odštevanja in časovni gumbi za časovnike stikal, urnike in gibljive luči',
            )
          }}
        </p>
      </div>
    </div>

    <article
      v-for="automation in automations"
      :key="automation.automationId"
      class="rounded-3xl border border-gray-200 bg-white/85 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/60">
      <!-- Header: progress ring + name/state + countdown -->
      <div class="flex items-center gap-4">
        <TimeSwitchDayRing
          v-if="automation.automationType === 'time_switch'"
          :config="automation.config"
          :color="accentColorFor(automation)"
          :size="54"
          :stroke-width="5" />
        <AutomationProgressRing
          v-else
          :ratio="automation.progressRatio"
          :control-icon="automationControlIcon(automation)"
          :color="accentColorFor(automation)"
          :size="54"
          :stroke-width="5" />

        <div class="min-w-0 flex-1">
          <h5 class="truncate text-base font-semibold text-gray-900 dark:text-white">{{ automation.name }}</h5>
          <p class="truncate text-sm text-gray-600 dark:text-gray-300">{{ automation.stateLabel }}</p>
        </div>

        <div
          class="shrink-0 rounded-2xl px-3 py-2 text-right"
          :class="
            automation.automationType === 'time_switch'
              ? 'bg-gradient-to-br from-sky-100 to-sky-50 dark:from-sky-500/25 dark:to-sky-500/5'
              : automation.automationType === 'light_motion_switch'
                ? 'bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-500/25 dark:to-amber-500/5'
                : 'bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-500/25 dark:to-emerald-500/5'
          ">
          <div class="text-lg font-semibold leading-none text-gray-900 dark:text-gray-100">
            {{ automation.countdownText || '—' }}
          </div>
          <div class="mt-1 text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {{
              automation.automationType === 'time_switch'
                ? tt('Next action', 'Naslednja akcija')
                : tt('Remaining', 'Preostalo')
            }}
          </div>
        </div>
      </div>

      <!-- Detail -->
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">{{ automation.detailLabel }}</p>

      <!-- Editor + Save: CONFIGURATION, so the dials, the schedule editor and
           the Save button are the admin's alone (cards t_bd611e14 /
           t_a4368691; D12: settings hidden, control shown). A non-admin keeps
           the status above — the countdown, state and detail lines. -->
      <template v-if="isAdmin">
        <div v-if="automation.automationType === 'switch_off_timer'" class="mt-4 flex justify-center">
          <RotaryTimerSelector
            :caption="tt('Timer duration', 'Trajanje časovnika')"
            :color="accentColorFor(automation)"
            :model-value="timerDuration(automation.automationId)"
            @update:model-value="setDuration(automation.automationId, 'on_duration_seconds', $event)" />
        </div>

        <div v-else-if="automation.automationType === 'light_motion_switch'" class="mt-4 flex justify-center">
          <RotaryTimerSelector
            :caption="tt('Off delay', 'Zamik izklopa')"
            :color="accentColorFor(automation)"
            :model-value="motionDelay(automation.automationId)"
            @update:model-value="setDuration(automation.automationId, 'delay_seconds', $event)" />
        </div>

        <div v-else-if="automation.automationType === 'time_switch'" class="mt-4 space-y-3">
          <TimeSwitchScheduleEditor
            :model-value="draftConfig(automation.automationId)"
            @update:model-value="setDraftConfig(automation.automationId, $event)" />
        </div>

        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
            :style="saveButtonStyle(automation)"
            :disabled="
              !isDirty(automation.automationId, automation.config) ||
              isSaving(automation.automationId) ||
              hasScheduleErrors(automation)
            "
            @click="saveAutomation(automation)">
            {{ isSaving(automation.automationId) ? tt('Saving…', 'Shranjevanje…') : saveLabel(automation) }}
          </button>
        </div>
      </template>
    </article>
  </div>
</template>

<script setup lang="ts">
import { tt } from '../../utils/consoleText';
import { computed, reactive, ref, watch } from 'vue';
import { apiFetch } from '../../services/api';
import { useAuthStore } from '../../stores/auth';
import type { SwitchAutomationDisplay } from '../../utils/switchAutomation';
import { getTimeSwitchScheduleErrors } from '../../utils/switchAutomation';
import type { HubConfigBag } from './deviceWidgetTypes';
import RotaryTimerSelector from '../inputs/RotaryTimerSelector.vue';
import AutomationProgressRing from './AutomationProgressRing.vue';
import TimeSwitchDayRing from './TimeSwitchDayRing.vue';
import TimeSwitchScheduleEditor from './TimeSwitchScheduleEditor.vue';

interface Props {
  automations: SwitchAutomationDisplay[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  updated: [];
}>();

const authStore = useAuthStore();

const draftConfigs = reactive<Record<string, HubConfigBag>>({});
const configSignatures = reactive<Record<string, string>>({});
const savingIds = ref<string[]>([]);

/**
 * Editing an automation is *configuration* of the house (card `t_bd611e14`):
 * the hub serves every device-automation mutation `authenticate +
 * authorize("admin")`, so the dials, the schedule editor and the Save button
 * belong to an admin (card `t_a4368691`; D12 — an automation schedule is
 * configuration, so the settings are not offered below that). The status
 * display above stays: the reads are `authenticate`, kept for exactly these
 * device sheets.
 */
const isAdmin = computed(() => authStore.isAdmin);

/**
 * The drawn glyph for the ring's middle (P11 sweep, card t_bd3aea71): the
 * control sheet speaks the sheet's icon set, so the hub's own Material icon
 * names (`automation.icon`) stop at the tile indicators and never reach this
 * panel. Types: this panel only ever receives the three switch-widget
 * automations (`SWITCH_WIDGET_AUTOMATION_TYPES`).
 */
function automationControlIcon(automation: SwitchAutomationDisplay): string {
  if (automation.automationType === 'light_motion_switch') return 'person';
  return 'timer';
}

watch(
  () =>
    props.automations.map((automation) => ({
      automationId: automation.automationId,
      signature: JSON.stringify(automation.config ?? {}),
      config: automation.config,
    })),
  (entries) => {
    for (const entry of entries) {
      if (configSignatures[entry.automationId] !== entry.signature) {
        draftConfigs[entry.automationId] = cloneConfig(entry.config);
        configSignatures[entry.automationId] = entry.signature;
      }
    }
  },
  { immediate: true },
);

function draftConfig(automationId: string) {
  if (!draftConfigs[automationId]) {
    draftConfigs[automationId] = {};
  }
  return draftConfigs[automationId];
}

function setDraftConfig(automationId: string, nextConfig: HubConfigBag) {
  draftConfigs[automationId] = cloneConfig(nextConfig);
}

function durationOf(automationId: string, key: string): number {
  const fallback = key === 'delay_seconds' ? 120 : 300;
  return Number(draftConfig(automationId)[key] ?? fallback);
}

function setDuration(automationId: string, key: string, rawValue: string | number) {
  const value = Number(rawValue);
  draftConfigs[automationId] = {
    ...draftConfig(automationId),
    [key]: Number.isFinite(value) && value > 0 ? value : 0,
  };
}

function timerDuration(automationId: string): number {
  return durationOf(automationId, 'on_duration_seconds');
}

function motionDelay(automationId: string): number {
  return durationOf(automationId, 'delay_seconds');
}

function isDirty(automationId: string, config: HubConfigBag): boolean {
  return JSON.stringify(draftConfig(automationId)) !== JSON.stringify(config ?? {});
}

function hasScheduleErrors(automation: SwitchAutomationDisplay): boolean {
  return (
    automation.automationType === 'time_switch' &&
    getTimeSwitchScheduleErrors(draftConfig(automation.automationId)).length > 0
  );
}

function isSaving(automationId: string): boolean {
  return savingIds.value.includes(automationId);
}

function accentColorFor(automation: SwitchAutomationDisplay): string {
  if (automation.automationType === 'time_switch') return 'rgb(14, 165, 233)';
  if (automation.automationType === 'light_motion_switch') return 'rgb(245, 158, 11)';
  return 'rgb(16, 185, 129)';
}

function saveButtonStyle(automation: SwitchAutomationDisplay) {
  const accent = accentColorFor(automation);
  return {
    background: `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 72%, black))`,
  };
}

function saveLabel(automation: SwitchAutomationDisplay): string {
  if (automation.automationType === 'time_switch') return tt('Save schedule', 'Shrani urnik');
  if (automation.automationType === 'light_motion_switch') return tt('Save delay', 'Shrani zamik');
  return tt('Save timer', 'Shrani časovnik');
}

async function saveAutomation(automation: SwitchAutomationDisplay) {
  if (hasScheduleErrors(automation)) {
    return;
  }

  const automationId = automation.automationId;
  savingIds.value = [...savingIds.value, automationId];

  try {
    await apiFetch(`/device-automations/${automationId}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: draftConfig(automationId),
      }),
    });
    emit('updated');
  } catch (error) {
    console.error(`[SwitchAutomationPanel] Failed to save automation ${automationId}`, error);
  } finally {
    savingIds.value = savingIds.value.filter((id) => id !== automationId);
  }
}

function cloneConfig(config: HubConfigBag): HubConfigBag {
  return JSON.parse(JSON.stringify(config ?? {}));
}
</script>
